'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { AuthStatusEnum, useAuth } from '@sola/auth-sdk/store.tsx';
import type { GraphQLSupporterAddress, GraphQLSupporterContact } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { UserError } from '@sola/shared-utils/errors-user.ts';
import { userInternalError } from '@sola/shared-utils/errors/supporters.ts';
import { isUserError } from '@sola/shared-utils/httpClient.ts';
import { MUTATION_KEYS, QUERY_KEYS } from '@sola/supporter-sdk/constants.ts';
import { useReadyForQueries, useSupporterMutation } from '@sola/supporter-sdk/hooks/base.ts';
import type { SupporterDetailsData } from '@sola/supporter-sdk/queries.ts';
import type { AccountSettingsMutationError, ContactInfoPayload, ContactInfoUpdateResponse } from './requests.ts';
import { type AddressUpdateResponse, updateSupporterAddress, updateSupporterContactInfo } from './requests.ts';
type AddressUpdateMutationVariables = {
  address: Omit<GraphQLSupporterAddress, 'isUndeliverable'>;
};

/**
 * Hook to update a supporter's mailing address.
 */
export const useAddressUpdate = () => {
  const $ = _c(10);
  const queryClient = useQueryClient();
  const {
    globalId
  } = useReadyForQueries();
  let t0;
  let t1;
  if ($[0] !== globalId) {
    t0 = [MUTATION_KEYS.addressUpdate, globalId];
    t1 = async t2 => {
      const {
        address
      } = t2;
      const response = await updateSupporterAddress({
        globalId: globalId as string,
        address
      });
      if (isUserError(response) || Array.isArray(response)) {
        throw response;
      }
      return response as AddressUpdateResponse;
    };
    $[0] = globalId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== globalId || $[4] !== queryClient) {
    t2 = result => {
      const normalizedAddress = result.data as GraphQLSupporterAddress;
      queryClient.setQueryData([QUERY_KEYS.supporterAddress, globalId], normalizedAddress);
    };
    $[3] = globalId;
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationKey: t0,
      mutationFn: t1,
      onSuccess: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useSupporterMutation(t3);
};
type UsernameUpdateMutationVariables = {
  preferredUsername: string;
};

/**
 * Hook to update a supporter's username.
 */
export const useUsernameUpdate = () => {
  const $ = _c(11);
  const queryClient = useQueryClient();
  const {
    globalId
  } = useReadyForQueries();
  const {
    authUser,
    actions
  } = useAuth("authUser", "actions");
  const t0 = authUser?.authUsername;
  let t1;
  if ($[0] !== t0) {
    t1 = [MUTATION_KEYS.preferredUsernameUpdate, t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== actions) {
    t2 = async t3 => {
      const {
        preferredUsername
      } = t3;
      await actions.updateUsername(preferredUsername);
      const status = actions.getCurrentStatus();
      if (status !== AuthStatusEnum.Success) {
        const error = actions.getError();
        throw error ?? userInternalError();
      }
      return {
        success: true,
        preferredUsername
      };
    };
    $[2] = actions;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== globalId || $[5] !== queryClient) {
    t3 = (_result, t4) => {
      const {
        preferredUsername: preferredUsername_0
      } = t4;
      queryClient.setQueryData([QUERY_KEYS.supporterPreferredUsername, globalId], preferredUsername_0);
    };
    $[4] = globalId;
    $[5] = queryClient;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      mutationKey: t1,
      mutationFn: t2,
      onSuccess: t3
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return useSupporterMutation(t4);
};
type PasswordUpdateMutationVariables = {
  password: string;
};

/**
 * Hook to update a supporter's password.
 */
export const usePasswordUpdate = () => {
  const $ = _c(7);
  const {
    authUser,
    actions
  } = useAuth("authUser", "actions");
  const t0 = authUser?.authUsername;
  let t1;
  if ($[0] !== t0) {
    t1 = [MUTATION_KEYS.passwordUpdate, t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== actions) {
    t2 = async t3 => {
      const {
        password
      } = t3;
      await actions.updatePassword(password);
      const status = actions.getCurrentStatus();
      if (status !== AuthStatusEnum.Success) {
        const error = actions.getError();
        throw error ?? userInternalError();
      }
      return {
        success: true
      };
    };
    $[2] = actions;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      mutationKey: t1,
      mutationFn: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useSupporterMutation(t3);
};
type ContactInfoUpdateMutationVariables = {
  contactId: string;
  contactInfo: ContactInfoPayload;
};

/**
 * Update any contact on the authenticated supporter's account. On
 * success, patches the three caches that can hold contact info:
 * `supporterDetails` (only if the update targets the primary),
 * `supporterContacts`, and `supporterContactPreferences` (patched by
 * matching `id`; left alone if the contact isn't in the array).
 */
export const useContactInfoUpdate = () => {
  const $ = _c(11);
  const queryClient = useQueryClient();
  const {
    globalId,
    contactId: primaryContactId
  } = useReadyForQueries();
  let t0;
  let t1;
  if ($[0] !== globalId) {
    t0 = [MUTATION_KEYS.contactInfoUpdate, globalId];
    t1 = async t2 => {
      const {
        contactId,
        contactInfo
      } = t2;
      const response = await updateSupporterContactInfo({
        contactId,
        globalId: globalId as string,
        contactInfo
      });
      if (isUserError(response) || Array.isArray(response)) {
        throw response;
      }
      return response as ContactInfoUpdateResponse;
    };
    $[0] = globalId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== globalId || $[4] !== primaryContactId || $[5] !== queryClient) {
    t2 = (_result, t3) => {
      const {
        contactId: contactId_0,
        contactInfo: contactInfo_0
      } = t3;
      const isPrimaryContact = !!primaryContactId && primaryContactId === contactId_0;
      if (isPrimaryContact) {
        queryClient.setQueryData([QUERY_KEYS.supporterDetails, globalId], existing => existing ? {
          ...existing,
          firstName: contactInfo_0.firstName,
          lastName: contactInfo_0.lastName,
          preferredName: contactInfo_0.preferredName ?? null,
          email: contactInfo_0.email,
          phone: contactInfo_0.phone ?? null
        } : existing);
      }
      const contactArrayKeys = [[QUERY_KEYS.supporterContacts, globalId], [QUERY_KEYS.supporterContactPreferences, globalId]] as const;
      for (const key of contactArrayKeys) {
        queryClient.setQueryData(key, existing_0 => {
          if (!existing_0) {
            return existing_0;
          }
          let didUpdate = false;
          const next = existing_0.map(c => {
            if (c.id !== contactId_0) {
              return c;
            }
            didUpdate = true;
            return {
              ...c,
              firstName: contactInfo_0.firstName,
              lastName: contactInfo_0.lastName,
              preferredName: contactInfo_0.preferredName ?? null,
              email: contactInfo_0.email,
              phone: contactInfo_0.phone ?? null
            };
          });
          return didUpdate ? next : existing_0;
        });
      }
    };
    $[3] = globalId;
    $[4] = primaryContactId;
    $[5] = queryClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      mutationKey: t0,
      mutationFn: t1,
      onSuccess: t2
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useSupporterMutation(t3);
};